import './pagination.css';
import React, { useState, useEffect, MutableRefObject } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { SEARCH_RESULTS_PAGE_PATH_STR } from '../../util/google.constants';
import { doubleLeftIcon, leftIcon, doubleRightIcon, rightIcon } from '../../util/icons';

export interface PaginationProps {
  resultsRef: MutableRefObject<HTMLDivElement | null>;
  activePage: number;
  maxButtons: number;
  itemsPerPage: number;
  totalItems: number;
}

const calcPages = (totalItems: number, itemsPerPage: number, maxButtons: number, activePage: number) => {
  if (totalItems === 0) {
    return { pages: [], totalPages: 0 };
  }
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const bufferPages = Math.ceil((maxButtons - 1) / 2);
  const highBufferPages = bufferPages;
  const pages = [];

  let lowBufferPages = bufferPages;
  if (maxButtons % 2 === 0) {
    lowBufferPages -= 1;
  }
  let highPageBound = activePage + highBufferPages;
  let lowPageBound = activePage - lowBufferPages;

  if (highPageBound > totalPages) {
    const leftOver = highPageBound - totalPages;
    highPageBound = totalPages;
    lowPageBound -= leftOver;
    if (lowPageBound < 1) {
      lowPageBound = 1;
    }
  }
  if (lowPageBound < 1) {
    const leftOver = Math.abs(lowPageBound) + 1;
    lowPageBound = 1;
    highPageBound += leftOver;
    if (highPageBound > totalPages) {
      highPageBound = totalPages;
    }
  }
  for (let i = lowPageBound; i <= highPageBound; i++) {
    pages.push(i);
  }

  return { pages, totalPages: totalPages };
};

export const Pagination: React.FC<PaginationProps> = props => {
  const [searchParams,] = useSearchParams();
  const [totalPages, setTotalPages] = useState<number>(0);
  const [pages, setPages] = useState<number[]>([]);
  const [prevPage, setPrevPage] = useState<number>(0);
  const [nextPage, setNextPage] = useState<number>(0);

  const location = useLocation();
  const currentPath = location.pathname;
  let currentPathNoPage = currentPath;

  const startIndex = currentPath.indexOf("/p_");
  
  if (startIndex !== -1) {
    currentPathNoPage = currentPath.substring(0, (startIndex + 1));
  }

  useEffect(() => {
    const tmp = calcPages(props.totalItems, props.itemsPerPage, props.maxButtons, props.activePage);
    setPages(tmp.pages);
    setTotalPages(tmp.totalPages);
    setPrevPage(props.activePage === 1 ? 1 : props.activePage - 1);
    setNextPage(props.activePage === tmp.totalPages ? tmp.totalPages : props.activePage + 1);
  }, [props.activePage, props.totalItems, props.itemsPerPage, props.maxButtons]);

  const searchUrl = currentPathNoPage + SEARCH_RESULTS_PAGE_PATH_STR;
  const searchUrlFirstPage = currentPathNoPage;
  const searchParamsStr = '?' + searchParams.toString();

  const pageUrl = (page: number) => {
    return (page === 1 ? searchUrlFirstPage : (searchUrl + page + '/')) + searchParamsStr;
  };

  const scrollTop = () => {
    props.resultsRef.current !== null && props.resultsRef.current.scrollIntoView();
  };

  if (totalPages < 2) {
    return (<></>);
  }
  
  // on bounds changed needs page reset in url
  return (
    <div id="pagination" aria-label="Page navigation" className="pagination">
      <Link to={pageUrl(1)} onClick={scrollTop} className={(props.activePage === 0 || props.activePage === 1) ? 'pagination-page disabled' : 'pagination-page'}>{doubleLeftIcon}</Link>
      <Link to={pageUrl(prevPage)} onClick={scrollTop} className={(props.activePage === 0 || props.activePage === 1) ? 'pagination-page disabled' : 'pagination-page'} >{leftIcon}</Link>
      {pages.map((page, i) =>
        <Link key={i} to={pageUrl(page)} onClick={scrollTop} className={page !== props.activePage ? 'pagination-page' : 'pagination-page active'} >{page}</Link>
      )}
      <Link to={pageUrl(nextPage)} onClick={scrollTop} className={props.activePage + 1 > totalPages ? 'pagination-page disabled' : 'pagination-page'} >{rightIcon}</Link>
      <Link to={pageUrl(totalPages)} onClick={scrollTop} className={props.activePage + 1 > totalPages ? 'pagination-page disabled' : 'pagination-page'}>{doubleRightIcon}</Link>
    </div>
  );
}

export default Pagination;